import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewsList = ({ reviews, overallRating, ratingBreakdown }) => {
  const [showAll, setShowAll] = useState(false);
  const displayedReviews = showAll ? reviews : reviews?.slice(0, 3);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      calendar: 'gregory'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? 'text-warning fill-warning' : 'text-muted-foreground/30'}
      />
    ));
  };

  // Get initials from customer name for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length > 1 
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Rating Summary */}
      <div className="bg-card border border-border rounded-xl p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Overall Rating */}
          <div className="text-center lg:text-right">
            <div className="text-5xl md:text-6xl font-bold text-foreground mb-2">
              {overallRating}
            </div>
            <div className="flex items-center justify-center lg:justify-end gap-1 mb-2">
              {renderStars(Math.round(overallRating))}
            </div>
            <p className="text-sm text-muted-foreground">
              بناءً على {reviews?.length} تقييم
            </p>
          </div>

          {/* Rating Breakdown */}
          <div className="space-y-2">
            {ratingBreakdown?.map((item) => (
              <div key={item?.stars} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground whitespace-nowrap w-12 text-left">
                  {item?.stars} نجوم
                </span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warning transition-smooth"
                    style={{ width: `${item?.percentage}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap w-12">
                  {item?.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Reviews List */}
      <div className="space-y-4 md:space-y-6">
        {displayedReviews?.map((review) => (
          <div
            key={review?.id}
            className="bg-card border border-border rounded-xl p-4 md:p-6"
          >
            <div className="flex items-start gap-4">
              {/* Customer Avatar - with initials fallback */}
              {review?.customerImage ? (
                <Image
                  src={review.customerImage}
                  alt={review?.customerImageAlt}
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0 border-2 border-primary/20">
                  <span className="text-primary font-bold text-sm md:text-base">
                    {getInitials(review?.customerName)}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground">
                      {review?.customerName}
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {formatDate(review?.date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {renderStars(review?.rating)}
                  </div>
                </div>
                {review?.comment && (
                  <p className="text-sm md:text-base text-foreground mb-3 leading-relaxed">
                    {review.comment}
                  </p>
                )}
                {review?.serviceType && (
                  <div className="flex items-center gap-2">
                    <Icon name="Camera" size={16} className="text-muted-foreground" />
                    <span className="text-xs md:text-sm text-muted-foreground">
                      {review.serviceType}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Show More Button */}
      {reviews?.length > 3 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
            iconName={showAll ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
          >
            {showAll ? 'عرض أقل' : `عرض جميع التقييمات (${reviews?.length})`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsList;